import { useState } from 'react';

const SAMPLE_QUESTIONS = [
  'What did I spend last quarter?',
  'Who are my top vendors, and how much with each?',
  'Break down my spend by tag.',
  'Summarize my invoices for March.',
  'List my most recent invoices.',
  'Show me the details of invoice #…',
];

interface Platform {
  id: string;
  name: string;
  tag?: string;
}

const PLATFORMS: Platform[] = [
  { id: 'claude', name: 'Claude', tag: 'RECOMMENDED · FULLY SUPPORTED' },
  { id: 'chatgpt', name: 'ChatGPT' },
  { id: 'perplexity', name: 'Perplexity / Grok / Mistral' },
  { id: 'developers', name: 'Developers' },
];

type PlatformId = string;

export default function McpSetupPage() {
  const [activePlatform, setActivePlatform] = useState<PlatformId>('claude');
  const [copied, setCopied] = useState(false);
  const mcpServerUrl = 'https://www.invoice-parse.com/mcp/v1/sse';

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(mcpServerUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="max-w-[1080px] mx-auto py-2 sm:py-6 px-2 sm:px-4">
      {/* Eyebrow & Title */}
      <div className="mb-6">
        <div className="text-[11.5px] font-mono font-semibold tracking-wider text-brand-vib uppercase dark:text-brand-light">
          / MCP CONNECTION
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold text-brand-navy dark:text-white tracking-tight mt-1">
          Connect your invoices to
          <br className="hidden sm:inline" /> any AI assistant
        </h1>
        <p className="text-sm sm:text-base text-brand-muted dark:text-bodydark max-w-2xl mt-2 leading-relaxed">
          Invoice Parse works as an MCP connector — link it to Claude, ChatGPT, and other AI
          tools, then ask questions about your invoices in plain language. No API keys. No code.
        </p>
      </div>

      {/* Server URL Card */}
      <div className="rounded-2.5xl p-6 mb-8 text-white shadow-hero relative overflow-hidden bg-gradient-to-r from-[#021B8D] via-[#0b3cb8] to-[#114FEE]">
        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="text-[10px] font-mono font-bold uppercase tracking-wider text-brand-light mb-1">
              YOUR MCP SERVER URL
            </div>
            <div className="font-mono text-sm sm:text-base font-semibold text-white tracking-wide break-all">
              {mcpServerUrl}
            </div>
          </div>

          <button
            type="button"
            onClick={handleCopyUrl}
            className="px-5 py-2.5 rounded-xl font-mono text-xs font-bold uppercase tracking-wider bg-brand-green text-brand-navy hover:bg-[#0fd48d] transition-colors shadow-sm inline-flex items-center justify-center gap-2 flex-shrink-0"
          >
            {copied ? (
              <>
                <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span>Copied!</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20">
                  <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                  <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                </svg>
                <span>Copy URL</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* 3 Value Prop Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
        <div className="rounded-2.5xl bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark p-5 shadow-card">
          <div className="flex items-center gap-2 mb-2 text-brand-vib">
            <svg className="w-5 h-5 fill-current" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <h3 className="text-sm font-bold text-brand-navy dark:text-white">
              Secure sign-in, no keys
            </h3>
          </div>
          <p className="text-xs text-brand-muted dark:text-bodydark leading-relaxed">
            You authorize with your Invoice Parse account; nothing to copy or paste into third-party servers.
          </p>
        </div>

        <div className="rounded-2.5xl bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark p-5 shadow-card">
          <div className="flex items-center gap-2 mb-2 text-emerald-600 dark:text-brand-green">
            <svg className="w-5 h-5 fill-current" viewBox="0 0 20 20">
              <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z" />
            </svg>
            <h3 className="text-sm font-bold text-brand-navy dark:text-white">
              Read-only
            </h3>
          </div>
          <p className="text-xs text-brand-muted dark:text-bodydark leading-relaxed">
            Your AI can read your invoice data to answer questions, but never change or delete original files.
          </p>
        </div>

        <div className="rounded-2.5xl bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark p-5 shadow-card">
          <div className="flex items-center gap-2 mb-2 text-brand-light">
            <svg className="w-5 h-5 fill-current" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012 6.012 0 011.912-2.706C6.512 5.73 6.974 6 7.5 6A1.5 1.5 0 019 7.5V8a2 2 0 004 0 2 2 0 011.523-1.943A5.977 5.977 0 0116 10c0 .34-.028.675-.083 1H15a2 2 0 00-2 2v2.197A5.973 5.973 0 0110 16v-.2c0-1.4-1.4-2.8-2.8-2.8H7a2 2 0 01-2-2v-1.232a1.996 1.996 0 00-.668-1.741z" clipRule="evenodd" />
            </svg>
            <h3 className="text-sm font-bold text-brand-navy dark:text-white">
              Works everywhere
            </h3>
          </div>
          <p className="text-xs text-brand-muted dark:text-bodydark leading-relaxed">
            Claude, ChatGPT, Perplexity, Grok, Mistral, Cursor, or any MCP-compatible client.
          </p>
        </div>
      </div>

      {/* What you can ask Section */}
      <div className="mb-10">
        <h2 className="text-lg font-bold text-brand-navy dark:text-white tracking-tight mb-4">
          What you can ask
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-2">
          {SAMPLE_QUESTIONS.map((q, idx) => (
            <div
              key={idx}
              className="flex items-center gap-3 p-3.5 rounded-2xl bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark text-xs sm:text-sm font-medium text-brand-navy dark:text-white shadow-sm"
            >
              <svg className="w-4 h-4 text-brand-vib fill-current flex-shrink-0" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
              </svg>
              <span>&ldquo;{q}&rdquo;</span>
            </div>
          ))}
        </div>
        <p className="text-xs font-mono text-brand-soft dark:text-bodydark2 mt-2">
          Your assistant answers from your real invoice data, live.
        </p>
      </div>

      {/* Step-by-Step by Platform */}
      <div className="rounded-2.5xl bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark p-6 sm:p-7 shadow-card mb-8">
        <h2 className="text-lg font-bold text-brand-navy dark:text-white tracking-tight mb-4">
          Step-by-step by platform
        </h2>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 pb-4 mb-6 border-b border-brand-line dark:border-strokedark">
          {PLATFORMS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setActivePlatform(p.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-mono font-semibold transition-colors ${
                activePlatform === p.id
                  ? 'bg-brand-vib text-white shadow-sm'
                  : 'bg-brand-bg dark:bg-strokedark text-brand-navy dark:text-white hover:bg-brand-blue-tint hover:text-brand-vib'
              }`}
            >
              <span>{p.name}</span>
              {p.tag && activePlatform === p.id && (
                <span className="hidden sm:inline-block text-[9px] font-mono px-2 py-0.5 rounded-full bg-white/20 text-white">
                  RECOMMENDED
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Steps Content */}
        <div className="space-y-4 text-sm text-brand-navy dark:text-white leading-relaxed">
          <div className="flex items-center gap-2 text-xs font-mono text-brand-vib dark:text-brand-light pb-2">
            <span>claude.ai · Desktop · Cowork · mobile</span>
            <span className="px-2 py-0.5 rounded-full bg-brand-green-tint text-[#0a6b4a] dark:bg-emerald-950/40 dark:text-emerald-300 font-bold text-[10px]">
              RECOMMENDED · FULLY SUPPORTED
            </span>
          </div>

          <ol className="space-y-3 list-decimal list-inside text-xs sm:text-sm text-brand-navy dark:text-bodydark1">
            <li>
              Go to <strong>Customize → Connectors</strong> (or{' '}
              <code className="px-1.5 py-0.5 rounded bg-brand-bg dark:bg-strokedark text-brand-vib font-mono text-xs">
                claude.ai/settings/connectors
              </code>
              ).
            </li>
            <li>
              Click the <strong>&ldquo;+&rdquo;</strong> next to Connectors →{' '}
              <strong>Add custom connector</strong>.
            </li>
            <li>
              Enter Name: <strong>Invoice Parse</strong> and paste the URL above.
            </li>
            <li>
              Click <strong>Add</strong> — you don&rsquo;t need OAuth Client ID/Secret under Advanced Settings; sign-in is automatic.
            </li>
            <li>
              Complete the Invoice Parse sign-in and authorize (read-only access).
            </li>
            <li>
              In any chat, click <strong>&ldquo;+&rdquo; → Connectors</strong> and toggle Invoice Parse on.
            </li>
          </ol>

          {/* Plans Notice Callout */}
          <div className="p-4 rounded-xl bg-brand-blue-tint/70 dark:bg-brand-900/30 border border-brand-vib/20 text-xs text-[#3b4569] dark:text-bodydark mt-5 leading-relaxed">
            <strong>Plans:</strong> Free (1 custom connector), Pro, Max, Team, Enterprise. On Team/Enterprise, an Owner must add it first under{' '}
            <code className="font-mono text-brand-vib">Organization settings → Connectors</code>; members then connect individually.
          </div>
        </div>
      </div>

      {/* Troubleshooting & Notes */}
      <div className="rounded-2.5xl bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark p-5 sm:p-6 shadow-card mb-8">
        <h3 className="text-xs font-mono font-bold uppercase tracking-wider text-brand-soft dark:text-bodydark2 mb-3">
          Troubleshooting & Notes
        </h3>
        <ul className="space-y-2 text-xs sm:text-sm text-brand-muted dark:text-bodydark list-disc list-inside leading-relaxed">
          <li>You must be signed in to Invoice Parse to authorize the connection.</li>
          <li>The connector only exposes data your account can already access.</li>
          <li>It&rsquo;s read-only — it cannot modify, upload, or delete anything.</li>
          <li>Connection is brokered from the AI provider&rsquo;s cloud, so the server must be reachable on the public internet (it is).</li>
        </ul>

        <div className="mt-4 pt-4 border-t border-brand-line/60 dark:border-strokedark text-xs text-brand-soft">
          Need a hand?{' '}
          <a
            href="mailto:support@invoice-parse.com"
            className="text-brand-vib hover:underline font-semibold"
          >
            Contact support
          </a>
          .
        </div>
      </div>
    </div>
  );
}
